import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Star, ShieldCheck, Sparkles, ChevronDown } from "lucide-react";
import { COMPANY, buildWhatsAppLink } from "@/data/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.2 } },
};
const item = {
  hidden: { y: 30, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const CHIPS = [
  { icon: ShieldCheck, label: `${COMPANY.years}+ Jahre Erfahrung` },
  { icon: Star, label: "Top bei Google bewertet" },
  { icon: Sparkles, label: "Modernste Technik" },
];

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative flex min-h-[100svh] items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video background */}
      <video
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        poster="https://images.pexels.com/photos/16898979/pexels-photo-16898979.jpeg?auto=compress&cs=tinysrgb&w=1600"
      >
        <source src="/hero-cleaning.mp4" type="video/mp4" />
      </video>

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050d24]/90 via-[#071336]/70 to-[#0a1a44]/40" />
      <div className="absolute inset-0 hero-vignette" />
      <div className="absolute inset-0 grain opacity-[0.06]" />

      {/* Animated color orbs */}
      <motion.div
        aria-hidden
        animate={{ y: [0, -24, 0], x: [0, 14, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -left-20 top-24 h-72 w-72 rounded-full bg-[var(--kilic-blue)]/40 blur-[110px]"
      />
      <motion.div
        aria-hidden
        animate={{ y: [0, 28, 0], x: [0, -18, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute bottom-10 right-0 h-80 w-80 rounded-full bg-[var(--kilic-green)]/35 blur-[120px]"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 pb-20 sm:px-6 md:px-8 md:pt-32">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-3xl">
          <motion.div
            variants={item}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md sm:px-4 sm:py-2 sm:text-xs"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--kilic-green)] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--kilic-green)]" />
            </span>
            Ihr Gebäudeservice in Bremen
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            Strahlende
            <br className="hidden sm:block" /> Sauberkeit für Ihr{" "}
            <span className="relative inline-block">
              <span className="text-[var(--kilic-green)]">Gebäude</span>
              <svg
                className="absolute -bottom-1.5 left-0 w-full sm:-bottom-2"
                viewBox="0 0 300 12"
                fill="none"
                preserveAspectRatio="none"
              >
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.9 }}
                  d="M2 9C60 4 240 4 298 9"
                  stroke="#4CAF2E"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 max-w-xl text-sm leading-relaxed text-white/80 sm:mt-8 sm:text-lg"
          >
            Glasreinigung, Hochdruck-, Fassaden- & Dachrinnenreinigung sowie
            Gartenpflege – professionell, zuverlässig und mit modernster Technik
            in {COMPANY.region}.
          </motion.p>

          <motion.div variants={item} className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center">
            <a
              href={buildWhatsAppLink({
                service: "eine Reinigung",
                message: "Ich hätte gern ein kostenloses Angebot.",
              })}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group flex items-center justify-center gap-3 rounded-full bg-[var(--kilic-green)] px-6 py-4 text-sm font-bold text-white shadow-xl shadow-green-900/30 transition-transform hover:scale-105 sm:text-base"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Kostenloses Angebot
            </a>
            <button
              onClick={() => scrollTo("rechner")}
              data-testid="hero-calc-btn"
              className="group flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-4 text-sm font-bold text-white backdrop-blur-md transition-colors hover:bg-white/20 sm:text-base"
            >
              Preis berechnen
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>

          {/* Trust chips */}
          <motion.div variants={item} className="mt-8 flex flex-wrap gap-2.5 sm:mt-10 sm:gap-3">
            {CHIPS.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-md sm:px-4 sm:py-2 sm:text-sm"
              >
                <Icon className="h-4 w-4 text-[var(--kilic-green)]" />
                {label}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("leistungen")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-1 text-white/70 md:flex"
        aria-label="Nach unten scrollen"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.2em]">Entdecken</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </motion.button>

      <a
        href={`tel:${COMPANY.phoneTel}`}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md lg:flex"
      >
        <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
      </a>
    </section>
  );
};
